/**
 * BottomNav — iOS-style fixed bottom tab bar shown on mobile (<md).
 *
 * Mobile PWA polish (2026-05-20):
 *   - Fixed to viewport bottom, respects iPhone home-indicator via pb-safe.
 *   - Active tab pulled from the URL (`location.pathname`).
 *
 * Wave-E (2026-06-10): Settings moved here from the burger drawer (the
 * drawer's duplicate nav block was removed). The tab row is now a
 * horizontally-scrollable strip of fixed-width tabs — it centres when
 * the tabs fit the viewport and scrolls when they overflow, so more
 * contours can be added later without cramming. The scrollbar is hidden.
 *   - The Files / Company tabs are admin-only; for non-admins they're
 *     hidden and the remaining tabs simply re-centre.
 *
 * Settings-V2 refinement (2026-06-18, founder spec; made permanent 2026-07-04):
 * the tab set is Inbox · Boards · Files · Brain (Company + Settings dropped;
 * Settings stays reachable via the account 'S' menu in the mobile drawer).
 *
 * Hidden on `md+` — desktop / tablet-landscape uses the original
 * ActivityBar left rail (rendered by <Shell> as before).
 *
 * Inbox unification (2026-07-11): the Inbox tab no longer NAVIGATES to the
 * /inbox route. It opens the shared <MentionsInbox> as a slide-up bottom
 * sheet (the same panel the desktop rail bell / tablet use), matching the
 * desktop "bell opens a popover, no navigation" logic. The always-mounted
 * controlled <MentionsInbox> here is also the single inbox WS subscription
 * for the mobile tab (proactive needs-you toast), replacing the one that used
 * to live in the flat-mobile drawer tree. The /inbox route stays intact as a
 * deep-link fallback.
 */
import { Link, useLocation } from '@tanstack/react-router';
import { Bell, Brain, FolderKanban, FolderTree, MessagesSquare, type LucideIcon } from 'lucide-react';
import * as React from 'react';

import { env } from '../../env';
import { useCanAdminOrg } from '../../lib/active-org';
import { inboxApi, inboxKeys } from '../../lib/api/inbox';
import { useQuery } from '@tanstack/react-query';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth-store';
import { MentionsInbox } from './mentions-inbox';

interface NavTab {
  key: string;
  label: string;
  icon: LucideIcon;
  to: string;
  /** Hide for non-admin users. */
  adminOnly?: boolean;
  /** Predicate to mark active (defaults to startsWith). */
  isActive?: (path: string) => boolean;
}

// Settings-V2 (2026-06-18, founder spec; permanent since 2026-07-04):
// slim mobile tab bar = Inbox (first, carries the unread badge) · Boards ·
// Files · Brain. Company is DROPPED (every /company/* page is re-homed in
// the Settings-V2 Company group). Settings is DROPPED too — it is reachable
// via the account 'S' menu in the mobile drawer (CompanyTree flat-mobile
// footer → ProfileMenu → Settings → /settings-v2).
const TABS_V2: NavTab[] = [
  {
    key: 'inbox',
    label: 'Inbox',
    icon: Bell,
    to: '/inbox',
    isActive: (p) => p.startsWith('/inbox'),
  },
  {
    key: 'boards',
    label: 'Boards',
    icon: FolderKanban,
    to: '/boards',
    isActive: (p) =>
      p.startsWith('/boards') || p.startsWith('/board/') || p === '/',
  },
  {
    key: 'files',
    label: 'Files',
    icon: FolderTree,
    to: '/files',
    adminOnly: true,
    isActive: (p) => p.startsWith('/files'),
  },
  {
    key: 'brain',
    label: 'Brain',
    icon: Brain,
    to: '/brain',
    isActive: (p) => p.startsWith('/brain'),
  },
];

// Communication Center tab — appended to the scrollable strip ONLY behind
// env.VITE_COMM_CENTER, so the OFF bar is byte-identical (no tab). The strip is
// built to grow (w-max + overflow-x-auto), so an extra tab just scrolls in.
const COMMS_TAB: NavTab = {
  key: 'comms',
  label: 'Comms',
  icon: MessagesSquare,
  to: '/comms',
  isActive: (p) => p.startsWith('/comms'),
};

export function BottomNav() {
  const location = useLocation();
  const canAdminOrg = useCanAdminOrg();
  const me = useAuthStore((s) => s.user);

  // Inbox tab opens the shared <MentionsInbox> as a slide-up sheet (below),
  // it does NOT navigate. Controlled here so the tab's active pill reflects
  // the sheet being open and Escape / tap-outside / X can close it.
  const [inboxOpen, setInboxOpen] = React.useState(false);

  // Inbox badge — the SAME global "open needs-you" count that MentionsInbox
  // (the left-rail bell + popover) uses, so the mobile tab badge matches the
  // desktop rail exactly (2026-07-11 alignment). GLOBAL across all orgs,
  // auth-only.
  const countsQ = useQuery({
    queryKey: inboxKeys.countsGlobal(),
    queryFn: () => inboxApi.countsGlobal(),
    enabled: !!me,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const unreadCount = countsQ.data ?? 0;

  // Settings-V2: slim tab set (Inbox · Boards · Files · Brain), plus the
  // Communication Center tab when its flag is ON.
  const tabs = env.VITE_COMM_CENTER ? [...TABS_V2, COMMS_TAB] : TABS_V2;
  const visible = tabs.filter((t) => !t.adminOnly || canAdminOrg);

  return (
    <nav
      data-testid="bottom-nav"
      aria-label="Primary mobile navigation"
      className={cn(
        // Fixed to viewport bottom on mobile only.
        'fixed inset-x-0 bottom-0 z-30 md:hidden',
        // Layered background to match the iOS-native vibe.
        'border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/75',
        // Pad bottom for the home-indicator + sides for the notch / rounded
        // corners in landscape (design-bug D4).
        'pb-safe px-safe',
      )}
    >
      {/* Horizontally-scrollable tab strip — TWO elements, and that is the
          whole point of this block.

          It used to be one: `flex w-max min-w-full ... overflow-x-auto` on the
          <ul>. A box cannot overflow itself. Sizing the element to its own
          content (`w-max`) means `scrollWidth` always equals `clientWidth`, so
          `overflow-x-auto` had nothing to do and the row simply spilled past
          the <nav> — off the right edge of the screen, with no gesture that
          brought it back. Measured on the shipped bundle at 360x800, the width
          most Android phones still report: five tabs at a fixed 76px = 380px of
          row, `ul.scrollLeft = 9999` settled back to 0, and the "Comms" tab sat
          at 304..380 on a 360px screen. The comment that used to be here said
          the row "scrolls once enough tabs are added"; it never did.

          Now the SCROLLER is the full-width parent and the ROW is the child:
          `w-max` still lets the row grow with the tab set, `min-w-full` still
          lets `justify-center` centre the tabs while they fit, and when they do
          not fit the parent has real overflow to scroll. Scrollbar hidden for a
          native feel; `overscroll-x-contain` keeps a horizontal swipe here from
          turning into a browser back-navigation. */}
      <div
        className={cn(
          'overflow-x-auto overscroll-x-contain',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        )}
      >
      <ul
        className="flex w-max min-w-full items-stretch justify-center"
        style={{ minHeight: '56px' }}
      >
        {visible.map((tab) => {
          const isInbox = tab.key === 'inbox';
          // The Inbox tab is "active" while its sheet is open (it never
          // navigates); every other tab reflects the current route.
          const isActive = isInbox
            ? inboxOpen
            : tab.isActive
              ? tab.isActive(location.pathname)
              : location.pathname.startsWith(tab.to);
          const Icon = tab.icon;
          const showBadge = isInbox && unreadCount > 0;

          const tabClassName = cn(
            'group relative flex h-full w-full flex-col items-center justify-center gap-0.5 px-1 py-2 text-[10px] font-medium leading-none transition-colors',
            isActive
              ? 'text-foreground'
              : 'text-muted-foreground hover:text-foreground active:text-foreground',
          );

          const inner = (
            <>
              <span className="relative inline-flex">
                <Icon
                  aria-hidden
                  className={cn(
                    'h-[22px] w-[22px] transition-transform',
                    isActive && 'scale-[1.05]',
                  )}
                />
                {showBadge && (
                  <span
                    aria-hidden
                    className="absolute -right-1.5 -top-1 inline-flex min-w-[16px] items-center justify-center rounded-full bg-foreground px-1 text-[9px] font-semibold leading-none text-background"
                    data-testid="bottom-nav-mentions-badge"
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </span>
              <span className="tracking-tight">{tab.label}</span>
              {/* Subtle active pill underline (iOS visual cue) */}
              {isActive && (
                <span
                  aria-hidden
                  className="absolute top-0 left-1/2 h-[2px] w-8 -translate-x-1/2 rounded-b bg-foreground"
                />
              )}
            </>
          );

          return (
            <li key={tab.key} className="w-[76px] shrink-0">
              {isInbox ? (
                // Opens the shared Inbox as a slide-up sheet — NO navigation
                // (mirrors the desktop rail bell). The sheet itself is the
                // <MentionsInbox> mounted below.
                <button
                  type="button"
                  aria-label={tab.label}
                  aria-haspopup="dialog"
                  aria-expanded={inboxOpen}
                  onClick={() => setInboxOpen(true)}
                  className={tabClassName}
                >
                  {inner}
                </button>
              ) : (
                <Link
                  to={tab.to}
                  aria-label={tab.label}
                  aria-current={isActive ? 'page' : undefined}
                  className={tabClassName}
                >
                  {inner}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
      </div>

      {/* Shared Inbox sheet — controlled by the Inbox tab above. Rendered
          here (always mounted on the mobile tab) so it is the single inbox
          WS subscription (proactive needs-you toast) and provides the
          slide-up bottom sheet. It portals to document.body, so its
          placement inside the nav does not affect layout. `hideTrigger`
          suppresses its own bell — the tab above is the trigger. */}
      <MentionsInbox hideTrigger open={inboxOpen} onOpenChange={setInboxOpen} />
    </nav>
  );
}
